import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarRange } from "lucide-react";
import { Button } from "../../components/ui/button/index.js";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover/index.js";
import { cn } from "../../lib/utils.js";

export function ReportDateRangeControl({
  dateFrom,
  dateTo,
  onDateRangeChange,
  fromTestId,
  toTestId,
  triggerTestId = "reports-dates-trigger",
}: {
  dateFrom: string;
  dateTo: string;
  onDateRangeChange: (from: string, to: string) => void;
  fromTestId?: string;
  toTestId?: string;
  triggerTestId?: string;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const hasRange = Boolean(dateFrom || dateTo);
  const formatYmd = (ymd: string) => {
    if (!ymd) return "";
    try {
      const [y, m, d] = ymd.split("-").map(Number);
      if (!y || !m || !d) return ymd;
      return new Intl.DateTimeFormat(i18n.language, {
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(new Date(y, m - 1, d));
    } catch {
      return ymd;
    }
  };
  const summary =
    dateFrom && dateTo
      ? `${formatYmd(dateFrom)} – ${formatYmd(dateTo)}`
      : dateFrom
        ? formatYmd(dateFrom)
        : dateTo
          ? formatYmd(dateTo)
          : t("reports.chrome.dates");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={cn(
            "h-8 max-w-[14rem] gap-1.5 truncate text-[0.75rem]",
            hasRange && "border-accent/30 bg-accent/5",
          )}
          data-testid={triggerTestId}
        >
          <CalendarRange size={14} className="shrink-0" aria-hidden="true" />
          <span className="truncate">{summary}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3">
        <div className="grid gap-3" data-testid="reports-dates-panel">
          <p className="m-0 text-[0.75rem] font-semibold text-foreground">
            {t("reports.chrome.dateRange")}
          </p>
          <label className="grid gap-1 text-[0.75rem]">
            <span className="text-muted">{t("reports.chrome.from")}</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(event) =>
                onDateRangeChange(event.target.value, dateTo || event.target.value)
              }
              className="h-9 rounded-md border border-line/70 bg-surface px-2.5 text-[0.8125rem] text-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent/50"
              data-testid={fromTestId}
            />
          </label>
          <label className="grid gap-1 text-[0.75rem]">
            <span className="text-muted">{t("reports.chrome.to")}</span>
            <input
              type="date"
              value={dateTo}
              onChange={(event) =>
                onDateRangeChange(dateFrom || event.target.value, event.target.value)
              }
              className="h-9 rounded-md border border-line/70 bg-surface px-2.5 text-[0.8125rem] text-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent/50"
              data-testid={toTestId}
            />
          </label>
        </div>
      </PopoverContent>
    </Popover>
  );
}
